import { getNthColor, getNthUserColor } from "@common/helpers/colors";
import { addCalendarDays } from "@common/helpers/date";
import { TaskPriority, TaskStatus, TaskType } from "@common/types/task";
import { formatDateOnly } from "@helpers/date";

export type DemoStageId =
    | "intake"
    | "prep"
    | "adjustments"
    | "review"
    | "deliver";

export interface DemoPerson {
    id: string;
    name: string;
    email: string;
    avatar: string | null;
    iconColor: string;
}

export interface DemoEntity {
    id: number;
    name: string;
    iconColor: string;
    logo: string | null;
}

export interface DemoClient {
    id: string;
    name: string;
}

/** Who works an In progress task: the Kick agent or a person. */
export type DemoWorker = "agent" | "person";

export interface DemoTask {
    id: string;
    title: string;
    stageId: DemoStageId | null;
    status: TaskStatus;
    inProgressBy: DemoWorker;
    type: TaskType;
    priority: TaskPriority | null;
    dueDateOnly: string | null;
    assigneeId: string | null;
    entityIds: readonly number[];
    /** The firm client the task is for: the owner of its first entity. */
    clientId: string | null;
    estimateMinutes: number | null;
    commentCount: number;
    closeName: string | null;
    /** First day of the month the task's close covers. */
    servicePeriodDateOnly: string | null;
    createdById: string | null;
    createdDateOnly: string;
}

interface DemoTaskRow extends Omit<
    DemoTask,
    | "id"
    | "dueDateOnly"
    | "priority"
    | "estimateMinutes"
    | "clientId"
    | "closeName"
    | "servicePeriodDateOnly"
    | "createdById"
    | "createdDateOnly"
    | "inProgressBy"
> {
    dueInDays: number | null;
    priority?: TaskPriority;
    estimateMinutes?: number;
    inProgressBy?: DemoWorker;
}

const PEOPLE_NAMES = [
    "Avery Chen",
    "Jordan Patel",
    "Riley Brooks",
    "Sam Ortiz",
    "Morgan Lee",
];

/** Photos the app already ships, so stacks show photos beside initials. */
const PEOPLE_PHOTOS: Readonly<Record<string, string>> = {
    "Avery Chen": "/images/thea.png",
    "Sam Ortiz": "/images/conrad.png",
};

export const DEMO_PEOPLE: readonly DemoPerson[] = PEOPLE_NAMES.map(
    (name, index) => ({
        id: `person-${index + 1}`,
        name,
        email: `${name.split(" ")[0].toLowerCase()}@demofirm.example`,
        avatar: PEOPLE_PHOTOS[name] ?? null,
        iconColor: getNthUserColor(index),
    }),
);

const ENTITY_NAMES = [
    "Northwind Holdings",
    "Bluebird Cafe",
    "Harbor Dental",
    "Pinecrest Properties",
];

export const DEMO_ENTITIES: readonly DemoEntity[] = ENTITY_NAMES.map(
    (name, index) => ({
        id: index + 1,
        name,
        iconColor: getNthColor(index),
        logo: null,
    }),
);

export const DEMO_CLIENTS: readonly DemoClient[] = [
    { id: "client-chen", name: "Chen Family Office" },
    { id: "client-main-street", name: "Main Street Partners" },
];

// Northwind and Pinecrest belong to the first client, the others to the second.
export const getEntityClientId = (entityId: number) =>
    entityId === 1 || entityId === 4 ? DEMO_CLIENTS[0].id : DEMO_CLIENTS[1].id;

const [AVERY, JORDAN, RILEY, SAM, MORGAN] = DEMO_PEOPLE.map(
    (person) => person.id,
);

const DEMO_TASK_ROWS: readonly DemoTaskRow[] = [
    // Demo Firm close tasks
    {
        title: "Collect and upload missing receipts",
        stageId: null,
        status: TaskStatus.TODO,
        type: TaskType.FILE,
        priority: TaskPriority.HIGH,
        estimateMinutes: 45,
        commentCount: 2,
        dueInDays: -3,
        assigneeId: AVERY,
        entityIds: [1],
    },
    {
        title: "Review unmatched transfers",
        stageId: null,
        status: TaskStatus.IN_PROGRESS,
        inProgressBy: "agent",
        type: TaskType.FILE,
        priority: TaskPriority.MEDIUM,
        estimateMinutes: 30,
        commentCount: 1,
        dueInDays: 2,
        assigneeId: JORDAN,
        entityIds: [2],
    },
    {
        title: "Close monthly books",
        stageId: null,
        status: TaskStatus.TODO,
        type: TaskType.FILE,
        priority: TaskPriority.URGENT,
        estimateMinutes: 60,
        commentCount: 4,
        dueInDays: 6,
        assigneeId: RILEY,
        entityIds: [1],
    },
    {
        title: "Review mortgage and rent allocations",
        stageId: null,
        status: TaskStatus.TODO,
        type: TaskType.FILE,
        priority: TaskPriority.HIGH,
        estimateMinutes: 50,
        commentCount: 3,
        dueInDays: 4,
        assigneeId: SAM,
        entityIds: [4],
    },
    {
        title: "Publish owner reporting packet",
        stageId: null,
        status: TaskStatus.TODO,
        type: TaskType.FILE,
        priority: TaskPriority.MEDIUM,
        estimateMinutes: 20,
        commentCount: 1,
        dueInDays: 9,
        assigneeId: MORGAN,
        entityIds: [3],
    },
    {
        title: "Review these uncats",
        stageId: null,
        status: TaskStatus.TODO,
        type: TaskType.TRANSACTIONS,
        commentCount: 2,
        dueInDays: null,
        assigneeId: AVERY,
        entityIds: [2],
    },
    {
        title: 'Get checking statement - "Chase Business Checking"',
        stageId: "intake",
        status: TaskStatus.TODO,
        type: TaskType.DOCUMENT_RETRIEVAL,
        commentCount: 1,
        dueInDays: -1,
        assigneeId: JORDAN,
        entityIds: [1],
    },
    {
        title: 'Get credit card statement - "Amazon Business Prime Amex Card"',
        stageId: "intake",
        status: TaskStatus.TODO,
        type: TaskType.DOCUMENT_RETRIEVAL,
        commentCount: 1,
        dueInDays: 1,
        assigneeId: JORDAN,
        entityIds: [1],
    },
    {
        title: 'Get checking statement - "Wise"',
        stageId: "intake",
        status: TaskStatus.DONE,
        type: TaskType.DOCUMENT_RETRIEVAL,
        commentCount: 2,
        dueInDays: -4,
        assigneeId: RILEY,
        entityIds: [2],
    },
    {
        title: 'Get credit card statement - "Amex Business Gold Card"',
        stageId: "intake",
        status: TaskStatus.TODO,
        type: TaskType.DOCUMENT_RETRIEVAL,
        commentCount: 1,
        dueInDays: 3,
        assigneeId: AVERY,
        entityIds: [3],
    },
    {
        title: "Uncategorized transaction review",
        stageId: "prep",
        status: TaskStatus.TODO,
        type: TaskType.TRANSACTION_REVIEW,
        commentCount: 3,
        dueInDays: 2,
        assigneeId: SAM,
        entityIds: [1],
    },
    {
        title: "Transactions without counterparty",
        stageId: "prep",
        status: TaskStatus.TODO,
        type: TaskType.TRANSACTIONS,
        commentCount: 1,
        dueInDays: 5,
        assigneeId: MORGAN,
        entityIds: [2],
    },
    {
        title: "Reconciliation",
        stageId: "prep",
        status: TaskStatus.TODO,
        type: TaskType.RECONCILIATION,
        commentCount: 2,
        dueInDays: 7,
        assigneeId: RILEY,
        entityIds: [4],
    },
    {
        title: "Material transaction review",
        stageId: "prep",
        status: TaskStatus.DONE,
        type: TaskType.TRANSACTIONS,
        commentCount: 4,
        dueInDays: -2,
        assigneeId: AVERY,
        entityIds: [3],
    },
    {
        title: "Record accrual expenses",
        stageId: "adjustments",
        status: TaskStatus.TODO,
        type: TaskType.QUESTION,
        commentCount: 1,
        dueInDays: 8,
        assigneeId: JORDAN,
        entityIds: [1],
    },
    {
        title: "Record deferred revenue adjustments",
        stageId: "adjustments",
        status: TaskStatus.TODO,
        type: TaskType.QUESTION,
        commentCount: 2,
        dueInDays: 8,
        assigneeId: SAM,
        entityIds: [3],
    },
    {
        title: "Post depreciation",
        stageId: "adjustments",
        status: TaskStatus.TODO,
        type: TaskType.QUESTION,
        commentCount: 1,
        dueInDays: 10,
        assigneeId: MORGAN,
        entityIds: [4],
    },
    {
        title: "Amortize prepaid expenses",
        stageId: "adjustments",
        status: TaskStatus.TODO,
        type: TaskType.QUESTION,
        commentCount: 3,
        dueInDays: -1,
        assigneeId: RILEY,
        entityIds: [2],
    },
    {
        title: "Review AP aging for upcoming obligations",
        stageId: "review",
        status: TaskStatus.TODO,
        type: TaskType.QUESTION,
        commentCount: 2,
        dueInDays: 12,
        assigneeId: AVERY,
        entityIds: [1],
    },
    {
        title: "Final review: balance sheet",
        stageId: "review",
        status: TaskStatus.TODO,
        type: TaskType.BALANCE_SHEET,
        commentCount: 4,
        dueInDays: 13,
        assigneeId: SAM,
        entityIds: [1],
    },
    {
        title: "Review AR aging for overdue items",
        stageId: "review",
        status: TaskStatus.TODO,
        type: TaskType.QUESTION,
        commentCount: 1,
        dueInDays: 11,
        assigneeId: JORDAN,
        entityIds: [3],
    },
    {
        title: "Final review: profit & loss",
        stageId: "review",
        status: TaskStatus.TODO,
        type: TaskType.PROFIT_LOSS,
        commentCount: 3,
        dueInDays: 13,
        assigneeId: MORGAN,
        entityIds: [2],
    },
    {
        title: "Send financial statements to client",
        stageId: "deliver",
        status: TaskStatus.TODO,
        type: TaskType.QUESTION,
        commentCount: 2,
        dueInDays: 15,
        assigneeId: RILEY,
        entityIds: [1],
    },
    // Extras: a blocked task, entity copies, spread across assignees
    {
        title: "Fix broken Mercury bank connection",
        stageId: "prep",
        status: TaskStatus.BLOCKED,
        type: TaskType.QUESTION,
        priority: TaskPriority.HIGH,
        commentCount: 4,
        dueInDays: -2,
        assigneeId: SAM,
        entityIds: [2],
    },
    {
        title: "Reconcile intercompany transfers",
        stageId: "adjustments",
        status: TaskStatus.IN_PROGRESS,
        inProgressBy: "agent",
        type: TaskType.RECONCILIATION,
        commentCount: 2,
        dueInDays: 3,
        assigneeId: AVERY,
        entityIds: [1, 2, 3],
    },
    {
        title: "Review payroll accruals",
        stageId: "review",
        status: TaskStatus.IN_PROGRESS,
        type: TaskType.QUESTION,
        commentCount: 1,
        dueInDays: 6,
        assigneeId: RILEY,
        entityIds: [1, 4],
    },
    {
        title: "Categorize owner draws",
        stageId: "prep",
        status: TaskStatus.IN_PROGRESS,
        inProgressBy: "agent",
        type: TaskType.TRANSACTIONS,
        commentCount: 3,
        dueInDays: 1,
        assigneeId: JORDAN,
        entityIds: [3],
    },
    {
        title: "Collect W-9s from 1099 vendors",
        stageId: "intake",
        status: TaskStatus.IN_PROGRESS,
        type: TaskType.DOCUMENT_RETRIEVAL,
        commentCount: 2,
        dueInDays: -5,
        assigneeId: MORGAN,
        entityIds: [2, 4],
    },
    {
        title: "Book loan interest",
        stageId: "adjustments",
        status: TaskStatus.DONE,
        type: TaskType.QUESTION,
        commentCount: 1,
        dueInDays: -3,
        assigneeId: SAM,
        entityIds: [4],
    },
    {
        title: "Confirm sales tax filings",
        stageId: null,
        status: TaskStatus.TODO,
        type: TaskType.QUESTION,
        priority: TaskPriority.LOW,
        commentCount: 1,
        dueInDays: 5,
        assigneeId: JORDAN,
        entityIds: [2, 3, 4],
    },
    {
        title: "Review fixed asset additions",
        stageId: "review",
        status: TaskStatus.TODO,
        type: TaskType.QUESTION,
        commentCount: 2,
        dueInDays: -1,
        assigneeId: AVERY,
        entityIds: [4],
    },
    {
        title: "Request prior-month statements",
        stageId: "intake",
        status: TaskStatus.CANCELLED,
        type: TaskType.FILE,
        commentCount: 1,
        dueInDays: -6,
        assigneeId: RILEY,
        entityIds: [3],
    },
    {
        title: "Draft management commentary",
        stageId: "deliver",
        status: TaskStatus.IN_PROGRESS,
        type: TaskType.QUESTION,
        commentCount: 3,
        dueInDays: 14,
        assigneeId: MORGAN,
        entityIds: [1, 2],
    },
];

/** Staged tasks belong to last month's close; the others to no close. */
const getCloseFields = (stageId: DemoStageId | null, todayDateOnly: string) => {
    if (stageId === null) {
        return { closeName: null, servicePeriodDateOnly: null };
    }
    const lastMonthEnd = addCalendarDays(`${todayDateOnly.slice(0, 8)}01`, -1);
    return {
        closeName: `${formatDateOnly(lastMonthEnd, "MMMM")} close`,
        servicePeriodDateOnly: `${lastMonthEnd.slice(0, 8)}01`,
    };
};

interface TaskDerivedFieldsArgs {
    stageId: DemoStageId | null;
    entityIds: readonly number[];
    dueInDays: number | null;
    todayDateOnly: string;
}

/** The fields that follow from a task's stage, entities and due offset. */
export const resolveTaskDerivedFields = ({
    stageId,
    entityIds,
    dueInDays,
    todayDateOnly,
}: TaskDerivedFieldsArgs) => ({
    ...getCloseFields(stageId, todayDateOnly),
    clientId: entityIds.length === 0 ? null : getEntityClientId(entityIds[0]),
    dueDateOnly:
        dueInDays === null ? null : addCalendarDays(todayDateOnly, dueInDays),
});

/** The demo close tasks with dates resolved against `todayDateOnly`. */
export const loadDemoTasks = (todayDateOnly: string): DemoTask[] =>
    DEMO_TASK_ROWS.map(
        (
            { dueInDays, priority, estimateMinutes, inProgressBy, ...row },
            index,
        ) => ({
            ...row,
            ...resolveTaskDerivedFields({
                stageId: row.stageId,
                entityIds: row.entityIds,
                dueInDays,
                todayDateOnly,
            }),
            id: `demo-task-${index + 1}`,
            inProgressBy: inProgressBy ?? "person",
            priority: priority ?? null,
            estimateMinutes: estimateMinutes ?? null,
            createdById: DEMO_PEOPLE[(index * 3) % DEMO_PEOPLE.length].id,
            createdDateOnly: addCalendarDays(todayDateOnly, -1 - (index % 12)),
        }),
    );
